import { Component } from "react";
import TagManager from "react-gtm-module";
import isEqual from "lodash/isEqual";

import history from "../../helpers/history";
import { GTM_ID } from "../../constants";

import { AnalyticsContext } from "./Analytics.context";
import AnalyticsAttribution from "./components/Analytics.attribution";

const withAnalytics = WrappedComponent => {
  const Analytics = class extends Component {
    constructor(props) {
      super(props);

      this.setDataLayerListingParameters =
        this.setDataLayerListingParameters.bind(this);
      this.setDataDataLayerSearchParameters =
        this.setDataLayerSearchParameters.bind(this);
      this.setDataDataLayerUserParameters =
        this.setDataLayerUserParameters.bind(this);
      this.trackEvent = this.trackEvent.bind(this);
      this.trackModalView = this.trackModalView.bind(this);
      this.trackOutboundLink = this.trackOutboundLink.bind(this);
      this.trackPageView = this.trackPageView.bind(this);

      this.state = {
        currentPath: null,
        currentUrl: null,
        isActive: false,
      };
    }

    /**
     * Set data layer listing parameters
     * @public
     */
    setDataLayerListingParameters() {
      TagManager.dataLayer({
        dataLayer: {
          ...this.props.dataLayerListingParameters,
        },
      });
    }

    /**
     * Set data layer listing parameters
     * @public
     */
    setDataLayerSearchParameters() {
      TagManager.dataLayer({
        dataLayer: {
          ...this.props.dataLayerSearchParameters,
        },
      });
    }

    /**
     * Set data layer user parameters
     * @public
     */
    setDataLayerUserParameters() {
      const traffic_type = this.props.authenticated
        ? "authenticated"
        : undefined;
      const user_id =
        this.props.authenticated && !!this.props.userId
          ? this.props.userId
          : undefined;
      const user_role =
        this.props.authenticated && !!this.props.userRole
          ? this.props.userRole
          : undefined;

      TagManager.dataLayer({
        dataLayer: {
          traffic_type,
          user_id,
          user_role,
        },
      });
    }

    /**
     * Track event
     * @param {object} event
     * @public
     */
    trackEvent(event, listings) {
      if (this.state.isActive && !!event) {
        const { category, action, label } = event;

        TagManager.dataLayer({
          dataLayer: {
            event: "customEvent",
            eventCategory: category,
            eventAction: action,
            eventLabel: label,
          },
        });
      }
    }

    /**
     * Track modal view
     * @param {string} modalName
     * @param {object} analyticsEvent
     * @public
     */
    trackModalView(modalName, analyticsEvent) {
      if (this.state.isActive && !!modalName) {
        const { currentPath } = this.state;
        const path = currentPath !== "/" ? currentPath : "";
        const modalPath = `${path}/${modalName}`;

        TagManager.dataLayer({
          dataLayer: {
            event: "customPageView",
            eventPath: modalPath,
          },
        });

        if (!!analyticsEvent) {
          this.trackEvent(analyticsEvent);
        }
      }
    }

    /**
     * Track outbound link clicks
     * @param {event} event
     * @param {object} analyticsEvent
     * @public
     */
    trackOutboundLink(event, analyticsEvent) {
      if (this.state.isActive && !!analyticsEvent) {
        event.preventDefault();
        const { action, label } = analyticsEvent;
        const eventTarget =
          !!event.target && event.target.hasAttribute("href")
            ? event.target
            : event.target.closest("a");
        const href = !!eventTarget && eventTarget.getAttribute("href");
        const hrefTarget = !!eventTarget && eventTarget.getAttribute("target");
        const category =
          !!href && href.indexOf("tel:") > -1 ? "click-to-call" : "click";

        const redirect = () =>
          handleRedirect(href, hrefTarget, !!event.metaKey);
        const fallback = setTimeout(redirect, 2100);

        TagManager.dataLayer({
          dataLayer: {
            event: "customEvent",
            eventCategory: category,
            eventAction: action,
            eventLabel: label,
            eventCallback: id => {
              if (id !== GTM_ID) {
                return;
              }

              if (!!fallback) {
                clearTimeout(fallback);
              }

              redirect();
            },
            eventTimeout: 2000,
          },
        });
      }
    }

    /**
     * Track page view
     * @param {object} location
     * @public
     */
    trackPageView(location) {
      const { currentUrl, isActive } = this.state;

      if (isActive) {
        const url = location.pathname + location.search;

        if (url !== currentUrl) {
          this.setState({ currentPath: location.pathname, currentUrl: url });
          TagManager.dataLayer({
            dataLayer: {
              event: "customPageView",
              eventPath: url,
            },
          });
        }

        if (!!location.state && !!location.state.analyticsEvent) {
          this.trackEvent(location.state.analyticsEvent);
          const nextState = { ...location.state };
          delete nextState.analyticsEvent;
          history.replace({ ...location, state: nextState });
        }
      }
    }

    componentDidMount() {
      if (!!history && !!GTM_ID) {
        TagManager.initialize({ gtmId: GTM_ID });

        this.setState({ isActive: true }, () => {
          this.unlisten = history.listen(this.trackPageView);
          this.trackPageView(history.location);
        });

        this.setDataLayerListingParameters();
        this.setDataLayerUserParameters();
      }
    }

    componentDidUpdate(prevProps) {
      if (
        !isEqual(
          prevProps.dataLayerListingParameters,
          this.props.dataLayerListingParameters
        )
      ) {
        this.setDataLayerListingParameters();
      }

      if (
        !isEqual(
          prevProps.dataLayerSearchParameters,
          this.props.dataLayerSearchParameters
        )
      ) {
        this.setDataLayerSearchParameters();
      }

      if (prevProps.authenticated !== this.props.authenticated) {
        this.setDataLayerUserParameters();
      }
    }

    componentWillUnmount() {
      if (this.state.isActive) {
        this.unlisten();
      }
    }

    render() {
      const { dataLayerListingParameters, dataLayerSearchParameters, ...rest } =
        this.props;
      return (
        <AnalyticsContext.Provider
          value={{
            trackEvent: this.trackEvent,
            trackModalView: this.trackModalView,
            trackOutboundLink: this.trackOutboundLink,
          }}
        >
          <WrappedComponent
            trackEvent={this.trackEvent}
            trackModalView={this.trackModalView}
            trackOutboundLink={this.trackOutboundLink}
            {...rest}
          />
          <AnalyticsAttribution />
        </AnalyticsContext.Provider>
      );
    }
  };

  return Analytics;
};

function handleRedirect(href, hrefTarget, isMetaKey) {
  if (hrefTarget === "_blank" || isMetaKey) {
    window.open(href, "_blank");
  } else {
    window.location = href;
  }

  return;
}

export default withAnalytics;
